import {
  Button,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { isEmptyString, isNullUndefined, tryParseFloat } from "../../util/util";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Cancel } from "@mui/icons-material";
import { getLeaseChargeCodes } from "../../services/lease.service";
import { useEffect, useState } from "react";
import { ResponsiveRow } from "../../constants/layout.constants";
import { useMemo } from "react";

export default function LeaseChargeItems({
  chargeItems,
  setChargeItems,
  chargeUnitsOptions,
  task,
  hasRevenueSharing,
}) {
  const [leaseChargeCodes, setLeaseChargeCodes] = useState([]);

  useEffect(() => {
    getLeaseChargeCodes().then((leaseChargeCodes) =>
      setLeaseChargeCodes(leaseChargeCodes)
    );
  }, []);

  // Calculate total using useMemo to avoid duplicates and unnecessary recalculations
  const chargeItemsTotal = useMemo(() => {
    const seen = new Set();
    return chargeItems.reduce((total, charge) => {
      const key = `${charge.unit?.id || ""}-${charge.chargeCode?.code || ""}`;
      if (!seen.has(key)) {
        seen.add(key);
        const amount = tryParseFloat(charge.amount, -1);
        if (amount > 0) {
          return total + amount;
        }
      }
      return total;
    }, 0);
  }, [chargeItems]);

  function handleChargeUnitChange(event, index) {
    let value = event.target.value;
    let newArr = [...chargeItems]; // copying the old items array
    newArr[index].unit.id = value;
    setChargeItems(newArr);
  }

  function handleChargeCodeChange(event, index) {
    let value = event.target.value;
    let newArr = [...chargeItems]; // copying the old items array
    newArr[index].chargeCode.code = value;
    if (newArr[index].revenueSharePercentage && value !== "REN") {
      const { revenueSharePercentage, ...rest } = newArr[index];
      newArr[index] = rest;
    }
    setChargeItems(newArr);
  }

  function leaseChargeAmountChange(event, index) {
    let value = event.target.value;
    let newArr = [...chargeItems]; // copying the old items array
    newArr[index].amount = value;
    setChargeItems(newArr);

  }

  function handleChargeStartDateChange(value, index) {
    let newArr = [...chargeItems]; // copying the old items array
    newArr[index].effectiveDate = value;
    setChargeItems(newArr);

  }

  function handleChargeTaxableToggle(event, index) {
    let value = event.target.checked;
    let newArr = [...chargeItems]; // copying the old items array
    newArr[index].taxable = value;
    setChargeItems(newArr);

  }

  function handleLeaseChargeItemRemove(index) {
    let newArr = [...chargeItems]; // copying the old items array
    newArr.splice(index, 1);
    setChargeItems(newArr);
  }

  function handleRevenueShareChange(event, index) {
    let value = event.target.value;
    let newArr = [...chargeItems]; // copying the old items array
    newArr[index].revenueSharePercentage = value;
    setChargeItems(newArr);
  }

  function leaseChargeNoteChange(event, index) {
    let value = event.target.value;
    let newArr = [...chargeItems]; // copying the old items array
    newArr[index].note = value;
    setChargeItems(newArr);
  }

  return (
    <>
      <TableContainer>
        <Table
          sx={{ minWidth: 650, marginBottom: "10px" }}
          aria-label="simple table"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "15%" }}>Unit *</TableCell>
              <TableCell sx={{ width: "15%" }}>Charge Type *</TableCell>
              <TableCell sx={{ width: "20%" }}>Amount *</TableCell>
              {hasRevenueSharing && (
                <TableCell sx={{ width: "20%" }}>Revenue Share(%)</TableCell>
              )}
              <TableCell sx={{ width: "20%" }}>Start Date *</TableCell>
              <TableCell sx={{ width: "10%" }}>Taxed?|Note</TableCell>
              <TableCell sx={{ width: "10%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chargeItems &&
              chargeItems.length > 0 &&
              chargeItems.map((charge, i) => {
                return (
                  <TableRow key={i}>
                    <TableCell>
                      <Select
                        sx={{ width: "90%" }}
                        id={"unit"}
                        value={charge.unit.id}
                        disabled={
                          task === "ActiveLeaseEdit" &&
                          (!isNullUndefined(charge.id) ||
                            !isEmptyString(charge.id))
                        }
                        onChange={(event) => handleChargeUnitChange(event, i)}
                      >
                        {chargeUnitsOptions && chargeUnitsOptions.length > 0 ? (
                          chargeUnitsOptions.map(function (unit, i) {
                            return (
                              <MenuItem key={i} value={unit.value}>
                                {unit.label}
                              </MenuItem>
                            );
                          }, this)
                        ) : (
                          <MenuItem sx={{ width: "100%" }}>
                            No Results Found
                          </MenuItem>
                        )}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        sx={{ width: "90%" }}
                        id={"chargeCode" + i}
                        value={charge.chargeCode.code}
                        disabled={
                          task === "ActiveLeaseEdit" &&
                          (!isNullUndefined(charge.id) ||
                            !isEmptyString(charge.id))
                        }
                        onChange={(event) => handleChargeCodeChange(event, i)}
                      >
                        {leaseChargeCodes && leaseChargeCodes.length > 0 ? (
                          leaseChargeCodes.map(function (chargeCode) {
                            let disabled = false;
                            for (let chargeValue of chargeItems) {
                              if (
                                charge.unit.id === chargeValue.unit.id &&
                                chargeValue.chargeCode.code === chargeCode.value
                              ) {
                                disabled = true;
                              }
                            }
                            return (
                              <MenuItem
                                key={chargeCode.value}
                                disabled={disabled}
                                value={chargeCode.value}
                              >
                                {chargeCode.label}
                              </MenuItem>
                            );
                          }, this)
                        ) : (
                          <MenuItem sx={{ width: "100%" }}>
                            No Results Found
                          </MenuItem>
                        )}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        sx={{ width: "90%" }}
                        id={"chargeAmount" + i}
                        name={"chargeAmount" + i}
                        isrequired={true}
                        disabled={false}
                        value={charge.amount}
                        onChange={(event) => leaseChargeAmountChange(event, i)}
                      />
                    </TableCell>
                    {hasRevenueSharing && (
                      <TableCell>
                        {charge.chargeCode.code === "REN" && (
                          <TextField
                            sx={{ width: "90%" }}
                            id={"revenueShare"}
                            name={"revenueShare"}
                            isrequired={
                              charge.chargeCode.code === "REN" ? true : false
                            }
                            disabled={
                              charge.chargeCode.code === "REN" ? false : true
                            }
                            value={charge?.revenueSharePercentage}
                            onChange={(event) =>
                              handleRevenueShareChange(event, i)
                            }
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="en-gb"
                      >
                        <DatePicker
                          sx={{ width: "90%" }}
                          id={"chargeStartDate"}
                          value={dayjs(charge.effectiveDate)}
                          disabled={true}
                          onChange={(newDate) =>
                            handleChargeStartDateChange(newDate, i)
                          }
                        />
                      </LocalizationProvider>
                    </TableCell>
                    <TableCell>
                      {(charge.chargeCode.code === "SERCH" ||
                        charge.chargeCode.code === "REN" ||
                        charge.chargeCode.code === "ELCUT") && (
                        <Switch
                          checked={charge.taxable}
                          onChange={(event) =>
                            handleChargeTaxableToggle(event, i)
                          }
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      )}
                      {(charge.chargeCode.code === "LFEE" ||
                        charge.chargeCode.code === "MISC") && (
                        <TextField
                          sx={{ width: "90%" }}
                          id={"chargeNote" + i}
                          name={"chargeNote" + i}
                          isrequired={true}
                          disabled={false}
                          value={charge.note}
                          onChange={(event) => leaseChargeNoteChange(event, i)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        type="submit"
                        sx={{ marginLeft: "10px" }}
                        onClick={() => handleLeaseChargeItemRemove(i)}
                        color="error"
                      >
                        <Cancel />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
          <ResponsiveRow container sx={{ width: "100%" }}>
            Total: {chargeItemsTotal}
          </ResponsiveRow>
        </Table>
      </TableContainer>
    </>
  );
}
